import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Detail = {
  unit: { id: string; code: string; name: string };
  outcomes: Array<{
    id: string;
    code: string;
    statement: string;
    sequence_no: number;
    cognitive_level?: string | null;
  }>;
};

type FormValues = {
  sequence_no: number;
  code: string;
  statement: string;
  cognitive_level?: string;
};

const LEVELS = ['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create', 'other'];

export default function CurriculumOutcomeFormPage() {
  const { t } = useTranslation('education');
  const { id: unitId, outcomeId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.curriculum.manage') || user?.permissions?.includes('education.admin')
  );
  const isNew = !outcomeId || outcomeId === 'new';
  const [form] = Form.useForm<FormValues>();

  const detail = useQuery({
    queryKey: ['education-curriculum-unit', unitId],
    enabled: Boolean(unitId),
    queryFn: async () => {
      const res = await api.get<Detail>(`/education/curriculum/units/${unitId}`);
      return res.data;
    },
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ sequence_no: (detail.data?.outcomes?.length || 0) + 1 });
      return;
    }
    const row = (detail.data?.outcomes ?? []).find((o) => o.id === outcomeId);
    if (!row) return;
    form.setFieldsValue({
      sequence_no: row.sequence_no,
      code: row.code,
      statement: row.statement,
      cognitive_level: row.cognitive_level || undefined,
    });
  }, [detail.data, form, isNew, outcomeId]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      isNew
        ? api.post(`/education/curriculum/units/${unitId}/outcomes`, values)
        : api.put(`/education/curriculum/units/${unitId}/outcomes/${outcomeId}`, values),
    onSuccess: () => {
      message.success(isNew ? t('curriculumOutcomeCreated') : t('curriculumOutcomeUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-curriculum-unit', unitId] });
      navigate(`/education/curriculum/units/${unitId}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={isNew ? t('newOutcome') : t('editOutcome')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('newOutcome') : t('editOutcome')}
      description={
        detail.data?.unit
          ? `${detail.data.unit.code} — ${detail.data.unit.name}`
          : t('curriculumUnitsIntro')
      }
      extra={
        <Button onClick={() => navigate(`/education/curriculum/units/${unitId}`)}>
          {t('backToUnit')}
        </Button>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }} loading={detail.isLoading}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('curriculumNoMarksHint')} />
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="sequence_no" label={t('sequence')} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="statement" label={t('outcomeStatement')} rules={[{ required: true }]}>
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Form.Item name="cognitive_level" label={t('cognitiveLevel')}>
            <Select
              allowClear
              options={LEVELS.map((level) => ({
                value: level,
                label: t(`cognitive_${level}` as 'cognitive_remember', { defaultValue: level }),
              }))}
            />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate(`/education/curriculum/units/${unitId}`)}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
